import en from 'src/locales/en.json';
import { copyKey, getOrganizationPresentation } from 'src/utils/presentation.util';
import type { CopyVariant, OrganizationPresentationType } from 'src/utils/presentation.util';

// NPT-2222: link-preview copy (+title.ts / +description.ts / +Head.tsx under
// pages/) picked by the org's presentation, so a cost-saving or neutral org's
// unfurl never claims proceeds. Strings live in en.json under `ssrMeta`; the
// SSR files do not run react-i18next, so this resolves and interpolates them
// directly. An org without `presentation` renders neutral.

/** The org fields link-preview copy reads. */
export interface PresentationOrg {
   name?: string | null;
   nickname?: string | null;
   beneficiary?: string | null;
   description?: string | null;
   presentation?: Partial<OrganizationPresentationType> | null;
}

type MetaOrg = PresentationOrg | null | undefined;

export type MetaVars = Record<string, string | number | null | undefined>;

/** Beneficiary, then nickname, then name, trimmed. '' when none is set. */
export const getMetaOrgName = (org: MetaOrg): string => org?.beneficiary?.trim() || org?.nickname?.trim() || org?.name?.trim() || '';

export const getMetaCopyVariant = (org: MetaOrg): CopyVariant => getOrganizationPresentation(org).copyVariant;

/** Resolves a dotted en.json key and fills `{{var}}` placeholders. Returns the key when it is missing. */
export function metaString(key: string, vars: MetaVars = {}): string {
   const value = key
      .split('.')
      .reduce<unknown>((node, part) => (node && typeof node === 'object' ? (node as Record<string, unknown>)[part] : undefined), en);
   if (typeof value !== 'string') return key;
   return value.replace(/{{\s*(\w+)\s*}}/g, (_, name: string) => {
      const v = vars[name];
      return v == null ? '' : String(v);
   });
}

/** One variant of a three-variant `ssrMeta.<base>` element. */
export const metaCopy = (base: string, variant: CopyVariant, vars: MetaVars = {}): string => metaString(copyKey(`ssrMeta.${base}`, variant), vars);

const orgCopy = (base: string, org: MetaOrg, vars: MetaVars = {}): string | null => {
   const orgName = getMetaOrgName(org);
   return orgName ? metaCopy(base, getMetaCopyVariant(org), { ...vars, orgName }) : null;
};

const truncate = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

// Each builder returns null when the org (or the subject it needs) is unknown,
// so the route keeps its own site-level fallback.

/** /c/:slug og:title. */
export const organizationMetaTitle = (org: MetaOrg, siteName: string): string | null => orgCopy('organization.title', org, { siteName });

/** /c/:slug description: the org's own description first. */
export const organizationMetaDescription = (org: MetaOrg, siteName: string): string | null =>
   org?.description?.trim() || orgCopy('organization.description', org, { siteName });

/** /c/:slug/hotels title (no site suffix). */
export const hotelsMetaTitle = (org: MetaOrg, placeName?: string | null): string | null => {
   const place = placeName?.trim();
   return place ? orgCopy('hotels.titleInPlace', org, { placeName: place }) : orgCopy('hotels.title', org);
};

/** /c/:slug/hotels description. */
export const hotelsMetaDescription = (org: MetaOrg, placeName?: string | null): string | null => {
   const place = placeName?.trim();
   return place ? orgCopy('hotels.descriptionInPlace', org, { placeName: place }) : orgCopy('hotels.description', org);
};

/** /c/:slug/hotels/:hotelId description when the hotel has none of its own. */
export const hotelMetaDescription = (hotelName: string | null | undefined, org: MetaOrg, siteName: string): string | null => {
   const name = hotelName?.trim();
   return name ? orgCopy('hotel.description', org, { hotelName: name, siteName }) : null;
};

/**
 * The org an event card speaks for: the org fetched by route slug, else the
 * event's nested org (name only, so it renders neutral).
 */
export const eventMetaOrganization = (
   organization: MetaOrg,
   event: { organization?: { name?: string | null } | null } | null | undefined,
): PresentationOrg | null => {
   if (getMetaOrgName(organization)) return organization ?? null;
   const name = event?.organization?.name?.trim();
   return name ? { name } : null;
};

/**
 * /c/:slug/events/:eventSlug description: the event's own description first.
 * With no org name there is no copy that names one, so the card shows the
 * event name alone rather than claiming support for a cause.
 */
export const eventMetaDescription = (
   event: { name?: string | null; title?: string | null; description?: string | null } | null | undefined,
   org: MetaOrg,
   siteName: string,
): string | null => {
   const own = event?.description?.trim();
   if (own) return truncate(own, 200);
   const eventName = event?.name?.trim() || event?.title?.trim();
   if (!eventName) return null;
   return orgCopy('event.description', org, { eventName, siteName }) ?? eventName;
};

/** /c/:slug/plan description. */
export const planMetaDescription = (org: MetaOrg): string | null => orgCopy('plan.description', org);

/** /c/:slug/plan/:programSlug description. Neutral team copy when no org is known. */
export const programPlanMetaDescription = (team: string | null | undefined, org: MetaOrg): string | null => {
   const subject = team?.trim();
   if (!subject) return null;
   return (
      orgCopy('plan.descriptionForTeam', org, { team: subject }) ?? metaCopy('plan.descriptionForTeam', 'neutral', { team: subject, orgName: '' })
   );
};

/** Game guide description when the guide has no overview. */
export const gameMetaDescription = (matchup: string | null | undefined, placeName: string | null | undefined, org: MetaOrg): string | null => {
   const subject = matchup?.trim();
   if (!subject) return null;
   const place = placeName?.trim();
   const where = place ? metaString('ssrMeta.game.where', { placeName: place }) : '';
   const copy = orgCopy('game.description', org, { matchup: subject, where }) ?? metaString('ssrMeta.game.descriptionNoOrg', { matchup: subject, where });
   return truncate(copy, 200);
};
